import { Injectable } from '@angular/core';
import { catchError, filter, map, merge, Observable, of, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';

import { CurrentResourceAbstractService } from '../../Basic/service/current-resource-abstract.service';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { AIApi } from '../../../api/ai/AI-api';
import { AiWorkflowTypesApiEnum } from '../../../api/ai/enums/ai-workflow-types-api.enum';
import { ResourceAction } from '../../Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';

import { BrandIdentityApiService } from '../../../api/brandIdentity/brand-identity.api.service';
import { AiBrandIdentityApi, BrandIdentityApi } from '../../../api/brandIdentity/models/brand-identity.api.model';
import { BrandIdentity } from '../models/brand-identity';

@Injectable()
export class BrandIdentityService extends CurrentResourceAbstractService<BrandIdentity, BrandIdentityApi, BrandIdentity, BrandIdentityApi> {

    // TODO: Remove when https://phabricator.agilitypr.internal/D38799 is landed.
    all$: Observable<BrandIdentity[]> = merge(
        this.fetchList$(),
        this.refreshAllManualSubject
    ).pipe(
        shareReplay(1)
    );

    constructor(
        public localizationService: LocalizationService,
        private messageService: MessageService,
        private branIdentityApiService: BrandIdentityApiService,
        private aiApi: AIApi
    ) {
        super();
    }

    override getApiService(): MultiBaseAbstractApi<BrandIdentityApi, BrandIdentityApi> {
        return this.branIdentityApiService;
    }

    override constructListModel(apiModel: BrandIdentityApi): BrandIdentity {
        return new BrandIdentity(apiModel);
    }

    override constructResourceModel(apiModel: BrandIdentityApi): BrandIdentity {
        return new BrandIdentity(apiModel);
    }

    protected fetchList$(): Observable<BrandIdentity[]> {
        return this.getApiService().getList().pipe(
            map(list => list
                .map(item => this.constructListModel(item))
                .sort((a, b) => a.orderId - b.orderId)
            ),
            catchError(error => {
                if (error.status === 404) {
                    return of([]);
                }
                throw error;
            })
        );
    }

    // TODO: Remove when https://phabricator.agilitypr.internal/D38799 is landed.
    override refreshAllManual(): Observable<BrandIdentity[]> {
        return this.fetchList$().pipe(
            tap(list => this.refreshAllManualSubject.next(list))
        );
    }

    aiCreate(
        brandName: string,
        broadcast: boolean = false
    ): Observable<BrandIdentity> {
        const resource = new BrandIdentity();
        resource.name = brandName;
        const action: ResourceAction = {
            actionType: ResourceActionTypes.CREATE,
            resource
        };
        const request = {
            name: brandName,
            requestType: AiWorkflowTypesApiEnum.BRAND_IDENTITY
        };
        return this.aiApi.runWorkflow(request).pipe(
            filter(x => !!x),
            map((brand: AiBrandIdentityApi) => {
                const brandModel = BrandIdentity.constructFromAiApiModel(brand);
                brandModel.name = brandName; // Override the name with user input to avoid confusion in case of AI generating a different name
                return brandModel;
            }),
            tap((brandModel: BrandIdentity) => {
                if (broadcast) {
                    action.resource = brandModel;
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw err;
            })
        );
    }

    create(
        brand: BrandIdentity,
        action: ResourceAction = {actionType: ResourceActionTypes.CREATE},
        broadCast: boolean = true
    ): Observable<BrandIdentity> {
        return this.fetchList$().pipe(
            switchMap(brandList => {
                const isNameDuplicated = brandList.some(
                    x => x.name.trim().toLocaleLowerCase() === brand.name.trim().toLocaleLowerCase()
                );
                if (isNameDuplicated) {
                    return throwError(() => new Error(CommonApiErrorEnum.duplicateName));
                }
                // newly created brand will be added to the end of the list
                const maxOrderId = brandList.reduce((order, item) => Math.max(order, item.orderId), -1);
                brand.orderId = maxOrderId + 1;
                // the first brand will be set as primary by default
                brand.primary = brandList.length === 0;
                return this.createResource(brand, action, broadCast);
            }),
            switchMap(id => {
                const linkedBrand = brand.linkedBrand;
                if (linkedBrand) {
                    const linkedCompetitor = linkedBrand.competitors?.find(x => x.name === brand.name);
                    if (linkedCompetitor) {
                        linkedCompetitor.id = id;
                        return this.update(linkedBrand, undefined, false).pipe(
                            map(() => id)
                        );
                    }
                }
                return of(id);
            }),
            switchMap(id => this.refreshAllManual().pipe(
                map(brands => {
                    const newBrand = brands?.find(item => item.id === id);
                    if (!newBrand) {
                        throw new Error(action.actionType);
                    }
                    return newBrand;
                })
            )),
            catchError(error => {
                this.handleErrors(error, brand);
                return throwError(() => error);
            })
        );
    }

    delete(brand: BrandIdentity): Observable<number> {
        return this.deleteResource(
            brand.id,
            {
                actionType: ResourceActionTypes.DELETE,
                resource: brand
            }
        ).pipe(
            switchMap(id => this.refreshAllManual().pipe(
                map(() => id)
            ))
        );
    }

    update(
        brand: BrandIdentity,
        action: ResourceAction = {actionType: ResourceActionTypes.UPDATE},
        broadCast: boolean = true
    ): Observable<BrandIdentity> {
        return this.isNameDuplicated$(brand.name, brand.id).pipe(
            switchMap(isDuplicated => isDuplicated
                ? throwError(() => new Error(CommonApiErrorEnum.duplicateName))
                : this.updateResource(brand, action, broadCast)
            ),
            switchMap(id => this.refreshAllManual().pipe(
                map(brands => {
                    const newBrand = brands?.find(item => item.id === id);
                    if (!newBrand) {
                        throw new Error(action.actionType);
                    }
                    return newBrand;
                })
            )),
            catchError(error => {
                this.handleErrors(error, brand);
                return throwError(() => error);
            })
        );
    }

    save(brand: BrandIdentity, action: ResourceAction = {actionType: ResourceActionTypes.SAVE}, broadCast: boolean = true) {
        return (brand.id || brand.id === 0) ? this.update(brand, action, broadCast) : this.create(brand, action, broadCast);
    }

    setOrder(brand: BrandIdentity, brandList: BrandIdentity[]): Observable<number[]> {
        return this.updateBulkResources(
            brandList,
            {
                actionType: ResourceActionTypes.UPDATE,
                resource: brand
            },
            false
        ).pipe(
            switchMap(id => this.refreshAllManual().pipe(
                map(() => id)
            ))
        );
    }

    handleErrors(error: Error, brand: BrandIdentity) {
        if (error?.message === CommonApiErrorEnum.duplicateName) {
            this.messageService.add({
                severity : messageSeverityEnum.error,
                summary: 'Common.brandIdentity.duplicateName.heading',
                detail: this.localizationService.transform('Common.brandIdentity.duplicateName.message', {
                    name: brand.name
                })
            });
        }
    }

    override messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as BrandIdentity;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (!error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Common.brandIdentity.create.success.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.create.success.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Common.brandIdentity.create.error.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.create.error.message', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (!error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Common.brandIdentity.update.success.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.update.success.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Common.brandIdentity.update.error.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.update.error.message', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.SAVE: {
                if (!error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Common.brandIdentity.save.success.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.update.success.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Common.brandIdentity.save.error.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.save.error.message', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.DELETE: {
                if (!error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Common.brandIdentity.delete.success.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.delete.success.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Common.brandIdentity.delete.error.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.delete.error.message', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
        }
    }
}
